<script setup lang="ts">
  import { computed } from 'vue';
  import type { ContentContainerProps } from './ContentContainer.types';
  import { ContentContainerVariant } from './ContentContainer.types';
  import {
    columnCellClass,
    fixedNarrowColumnWrapperClass,
    focusedColumnWrapperClass,
    fullColumnWrapperClass,
    primarySecondaryCellClass,
    rootClass,
    rootVariantClasses,
  } from './ContentContainer.css';

  const props = withDefaults(defineProps<ContentContainerProps>(), {
    variant: ContentContainerVariant.Full,
  });

  const resolvedTestId = computed(() => props.testId ?? 'ds-content-container');

  const isFull = computed(() => props.variant === ContentContainerVariant.Full);
  const isFocused = computed(
    () => props.variant === ContentContainerVariant.Focused,
  );
  const isSplit = computed(
    () => props.variant === ContentContainerVariant.Split,
  );
  const isPrimarySecondary = computed(
    () => props.variant === ContentContainerVariant.PrimarySecondary,
  );
  const isFixedNarrow = computed(
    () => props.variant === ContentContainerVariant.FixedNarrow,
  );
  const isFocusedCentered = computed(
    () => props.variant === ContentContainerVariant.FocusedCentered,
  );

  const rootClasses = computed(() => [
    rootClass,
    rootVariantClasses[props.variant],
  ]);
</script>

<template>
  <div :class="rootClasses" :data-testid="resolvedTestId">
    <div v-if="isFull" :class="[fullColumnWrapperClass, props.contentClass]">
      <slot />
    </div>
    <div
      v-else-if="isFocused"
      :class="[focusedColumnWrapperClass, props.contentClass]"
    >
      <slot />
    </div>
    <div
      v-else-if="isFocusedCentered"
      :class="[focusedColumnWrapperClass, props.contentClass]"
    >
      <slot />
    </div>
    <div
      v-else-if="isFixedNarrow"
      :class="[fixedNarrowColumnWrapperClass, props.contentClass]"
    >
      <slot />
    </div>

    <template v-else-if="isSplit">
      <div :class="columnCellClass"><slot name="left" /></div>
      <div :class="columnCellClass"><slot name="right" /></div>
    </template>

    <template v-else-if="isPrimarySecondary">
      <div :class="primarySecondaryCellClass"><slot name="primary" /></div>
      <div :class="primarySecondaryCellClass"><slot name="secondary" /></div>
    </template>
  </div>
</template>
